import { Dispatch, SetStateAction, useCallback } from 'react';

import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';

import { getFilenameExtension } from '../../../utils/functions';
import Button from '../button';

import './DragAndDropButton.scss';

export interface DragAndDropButtonProps {
  setFile: Dispatch<SetStateAction<File | 'DELETE_ONLY' | undefined>>;
  maxFileSize: number;
  allowedFileTypes: string[];
  disabled?: boolean;
  className?: string;
}

function DragAndDropButton({
  setFile,
  maxFileSize,
  allowedFileTypes,
  disabled = false,
  className = '',
}: DragAndDropButtonProps) {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 1) {
        alert('Upload only one file');
      } else {
        const fileExtension = getFilenameExtension(acceptedFiles[0].name);
        const fileSizeInMb = acceptedFiles[0].size / 1000000;
        const extensionValid = allowedFileTypes.includes(fileExtension);
        const fileSizeValid = fileSizeInMb < maxFileSize;

        let errorMessage = '';
        if (!extensionValid) {
          errorMessage += 'The file type is not supported\n';
        }
        if (!fileSizeValid) {
          errorMessage += 'The file size is too big';
        }
        if (extensionValid && fileSizeValid) {
          setFile(acceptedFiles[0]);
        } else {
          alert(errorMessage);
          setFile(undefined);
        }
      }
    },
    [setFile, allowedFileTypes, maxFileSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  });
  const { ref, ...rootProps } = getRootProps();
  const appliedRootProps = disabled ? {} : rootProps;

  return (
    <Button
      className={`drag-and-drop-button ${className}`}
      type="button"
      disabled={disabled}
      innerRef={ref}
      {...appliedRootProps}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Drop the file here...</p>
      ) : (
        <>
          <Icon icon="ic:outline-cloud-upload" width="2.5rem" height="2.5rem" />
          <p className="text-lg">Click to upload or drag and drop</p>
          <p className="text-sm">(max size {maxFileSize} MB)</p>
        </>
      )}
    </Button>
  );
}

export default DragAndDropButton;
